<template>
  <el-form-item
    :label="titlize(label)"
    :rules="metaData.is_required ? requiredField() : []"
  >
    <div class="el-input" :class="{ 'is-disabled': preview }">
      <input
        :ref="`input-field-${formIndex}`"
        class="el-input__inner"
        :type="fieldType"
        :id="metaData.name"
        :name="metaData.name"
        v-model="parentForm[metaData.name]"
        @focus="changeColor($event)"
        @blur="resetColor($event)"
        @input="validateInput()"
        @keyup="checkCapslock($event)"
        :disabled="preview"
      />

      <span v-if="fieldType === 'password'" class="el-input__suffix">
        <span class="el-input__suffix-inner">
          <i
            class="el-input__icon el-icon-view el-input__clear"
            @click="togglePasswordVisibility(formIndex)"
          ></i>
        </span>
        <i
          class="el-input__icon el-input__validateIcon el-icon-circle-check"
        ></i>
      </span>
      <div class="el-form-item__error">
        <span v-if="capsLockOn" class="caps-warning">{{ capsLockMsg }}</span>
        {{ errMsg }}
      </div>
    </div>
  </el-form-item>
</template>

<script>
import { titleCase } from "@/lib/utils";

export default {
  name: "TextInput",
  props: {
    label: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    metaData: {
      type: Object,
      default: () => {
        return {};
      }
    },
    fieldTypes: {
      type: Array,
      default() {
        return [];
      }
    },
    colors: {
      type: Object,
      default() {
        return {};
      }
    },
    parentForm: {
      type: Object,
      default: () => {
        return {};
      }
    },
    formIndex: {
      type: Number,
      default: 0
    },
    loadFieldFrom: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      errMsg: "",
      capsLockOn: false,
      capsLockMsg: "WARNING! Caps lock is ON.",
      form: {
        input: ""
      },
      input: ""
    };
  },
  inject: ["preview"],
  computed: {
    fieldType() {
      if (this.metaData.is_password_type) {
        return "password";
      }
      const typeId = this.metaData.field_type_id;
      const fieldType = this.fieldTypes.find(e => e.id === typeId);
      let inputType = "text";
      switch (fieldType.label) {
        case "Text":
          return "text";
        case "Number":
          inputType = "number";
          break;
        case "Email":
          inputType = "email";
          break;
        case "Password":
          inputType = "password";
          break;
        default:
          inputType = "text";
          break;
      }

      return inputType;
    },
    preview() {
      return this.preview;
    }
  },

  methods: {
    togglePasswordVisibility(index) {
      const inputElt = this.$refs[`input-field-${index}`];
      inputElt.type = inputElt.type === "text" ? "password" : "text";
    },

    isUpper(str) {
      const uppercaseExp = new RegExp("^(?=.*[A-Z]).+$");
      return uppercaseExp.test(str);
    },

    isSpecialChar(str) {
      const splcaseExp = new RegExp("^(?=.*[-+_!@#$%^&*., ?]).+$");
      return splcaseExp.test(str);
    },

    validateInput() {
      const inputVal = this.$refs[`input-field-${this.formIndex}`].value;
      if (!this.metaData.is_required && !inputVal.length) {
        return;
      }

      if (this.metaData.is_required && !inputVal.length) {
        this.errMsg = `${this.titlize(this.label)} is required`;
        this.$emit("isValid", false);
        return;
      } else if (
        this.metaData.field_type &&
        this.metaData.field_type.validation_regex
      ) {
        const regx = new RegExp(this.metaData.field_type.validation_regex, "g");
        const validInput = regx.test(inputVal);
        this.errMsg = !validInput
          ? `${this.titlize(this.label)} is invalid`
          : "";
        if (!validInput) {
          this.$emit("isValid", false);
        }
      } else if (
        this.metaData.password_validation &&
        this.loadFieldFrom !== "login"
      ) {
        const validation = this.metaData.password_validation;
        let nestedValid = true;
        const numregx = /\d/;
        if (inputVal.length < validation.min_length) {
          this.errMsg = `Use ${validation.min_length} or more characters`;
          nestedValid = false;
        } else if (validation.include_numbers && !numregx.test(inputVal)) {
          this.errMsg = "Use a number(e.g. 1234)";
          nestedValid = false;
        } else if (validation.include_uppercase && !this.isUpper(inputVal)) {
          this.errMsg = "Use upper and lower case letters(e.g Aa)";
          nestedValid = false;
        } else if (
          validation.include_special &&
          !this.isSpecialChar(inputVal)
        ) {
          this.errMsg = "Use a symbol(e.g. !@#$)";
          nestedValid = false;
        } else {
          nestedValid = true;
          this.errMsg = "";
        }

        if (!nestedValid) {
          this.$emit("isValid", false);
        }
      } else {
        this.errMsg = "";
      }
    },

    resetColor(evt) {
      this.validateInput();
      evt.target.style.borderColor = "#dcdfe6";
    },

    changeColor(evt) {
      evt.target.style.borderColor = this.colors.secondary;
    },

    checkCapslock(event) {
      if (this.fieldType !== "password") {
        return;
      }
      try {
        this.capsLockOn = event.getModifierState("CapsLock");
      } catch {
        return;
      }
    },

    titlize(option) {
      return titleCase(option);
    },

    labelToKey(label) {
      return label.toLocaleLowerCase().replace(/ /gi, "_");
    }
  }
};
</script>

<style lang="scss" scoped>
.el-form-item__label {
  color: red !important;
}

.caps-warning {
  color: #e6a23c !important;
}
</style>
